/**
 * Поиск адреса через HTTP-геокодер Яндекса (geocode-maps.yandex.ru).
 *
 * Раньше адрес искали через ymaps.geocode внутри JS API 2.1, но тот ходит в свой сервис поиска,
 * который принимает только старые ключи «JavaScript API и HTTP Геокодер». В новом кабинете
 * Яндекса (yandex.ru/maps-api/console) у каждого продукта свой ключ, и ни ключ «JavaScript API»,
 * ни ключ «API Геокодера» там не работают. HTTP-геокодер принимает и старые ключи, и ключ
 * «API Геокодера», а карту JS API 2.1 с таким ключом тоже рисует — поэтому ключ остаётся один.
 */

const GEOCODER_URL = 'https://geocode-maps.yandex.ru/1.x/'

// Координаты, вставленные из Яндекс.Карт/Google: «40.758470, 72.370147», «40.758470 72.370147»,
// «40,758470 72,370147». Возвращает {latitude, longitude} либо null, если это обычный текст.
export function parseCoordinates(value) {
	const raw = String(value ?? '').trim().replace(/[()[\]]/g, ' ').trim()
	if (!raw) return null

	var latitude
	var longitude
	// сначала вариант с десятичной запятой — там разделителем может быть только пробел
	var match = raw.match(/^(-?\d{1,3},\d+)\s+(-?\d{1,3},\d+)$/)
	if (match) {
		latitude = Number(match[1].replace(',', '.'))
		longitude = Number(match[2].replace(',', '.'))
	} else {
		match = raw.match(/^(-?\d{1,3}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)$/)
		if (!match) return null
		latitude = Number(match[1])
		longitude = Number(match[2])
	}

	if (!isFinite(latitude) || !isFinite(longitude)) return null
	// два целых числа («12, 5») — это скорее номер дома, а не координаты
	if (Number.isInteger(latitude) && Number.isInteger(longitude)) return null
	// если первой пришла долгота (ссылки Яндекса отдают «долгота,широта») — меняем местами
	if (Math.abs(latitude) > 90 && Math.abs(longitude) <= 90) {
		const swap = latitude
		latitude = longitude
		longitude = swap
	}
	if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null

	return { latitude, longitude }
}

// Объект из ответа геокодера → координаты, полный и короткий адрес.
// Полный адрес — без страны, как его отдавал getAddressLine() в JS API.
function parseGeoObject(geoObject) {
	const [longitude, latitude] = String(geoObject?.Point?.pos || '').split(' ').map(Number)
	if (!isFinite(latitude) || !isFinite(longitude)) return null

	const meta = geoObject.metaDataProperty?.GeocoderMetaData || {}
	const components = meta.Address?.Components || []
	const component = (kind) => components.find((item) => item.kind === kind)?.name || ''

	const country = component('country')
	let fullAddress = meta.text || meta.Address?.formatted || geoObject.name || ''
	if (country && fullAddress.startsWith(`${country}, `)) {
		fullAddress = fullAddress.slice(country.length + 2)
	}

	const locality = component('locality')
	const street = component('street')
	const house = component('house')

	return {
		latitude,
		longitude,
		full_address: fullAddress,
		// короткий адрес: улица с домом, иначе город, иначе полный адрес
		short_address: [street, house].filter(Boolean).join(', ') || locality || fullAddress,
		locality,
		street,
		house,
	}
}

/**
 * query — строка (поиск по названию) или [широта, долгота] (адрес по точке).
 * boundedBy — [[минШирота, минДолгота], [максШирота, максДолгота]], как у getYandexSearchBounds;
 * strictBounds — искать только внутри рамки.
 * Возвращает список адресов; при отказе Яндекса (неверный ключ, лимит) бросает ошибку.
 */
export async function yandexGeocode(apiKey, query, { results = 1, boundedBy = null, strictBounds = false, lang = 'ru_RU' } = {}) {
	const key = String(apiKey || '').trim()
	if (!key) throw new Error('map_token_empty')

	const params = new URLSearchParams({ apikey: key, format: 'json', lang, results: String(results) })
	if (Array.isArray(query)) {
		// sco=latlong — координаты передаём в привычном порядке «широта,долгота»
		params.set('geocode', `${query[0]},${query[1]}`)
		params.set('sco', 'latlong')
	} else {
		params.set('geocode', String(query || '').trim())
	}
	if (boundedBy) {
		// bbox у геокодера в порядке «долгота,широта~долгота,широта»
		params.set('bbox', `${boundedBy[0][1]},${boundedBy[0][0]}~${boundedBy[1][1]},${boundedBy[1][0]}`)
		if (strictBounds) params.set('rspn', '1')
	}

	const response = await fetch(`${GEOCODER_URL}?${params.toString()}`)
	if (!response.ok) throw new Error(`geocoder_${response.status}`)

	const json = await response.json()
	const members = json?.response?.GeoObjectCollection?.featureMember || []
	return members.map((member) => parseGeoObject(member.GeoObject)).filter(Boolean)
}

/** Адрес по точке: первый найденный объект или null. */
export async function yandexReverseGeocode(apiKey, latitude, longitude) {
	const list = await yandexGeocode(apiKey, [latitude, longitude], { results: 1 })
	return list[0] || null
}
